import { Component, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalController, PickerController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';

import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { Contact } from '../models/contact';
import { Business } from '../models/business';
import { Job } from '../models/job';
import { WorkItem } from '../models/work-item';
import { Invoice } from '../models/invoice';
import { Quote } from '../models/quote';

import { ApiService } from '../services/api.service';
import { AuthService } from '../services/auth.service';

import { Router } from '@angular/router';

import { ContactsSelectorComponent } from '../contacts-selector/contacts-selector.component';
import { BusinessSelectorComponent } from '../business-selector/business-selector.component';
import { PropertyChooserComponent } from '../property-chooser/property-chooser.component';

import { Property } from '../models/property';

@Component({
  selector: 'app-job-new',
  templateUrl: './job-new.page.html',
  styleUrls: ['./job-new.page.scss'],
  providers: [ ]
})
export class JobNewPage implements OnInit {

  chosenContact: Contact;
  isNativePlatform = true;
  ionicForm: UntypedFormGroup;
  isSubmitted = false;
  properties = [];
  newJob: Job;
  newInvoice: Invoice;
  newQuote: Quote;
  chosenBusiness: Business;
  accountCurrency: string;
  @Input() public contactId: number;
  createQuote: boolean = false;
  createInvoice: boolean = false;
  loadingFromAI: boolean = false;
  isProcessing: boolean = false;
  
  workItems: WorkItem[] = [];

  constructor(
    public modalCtrl: ModalController, 
    public pickerCtrl: PickerController, 
    public formBuilder: UntypedFormBuilder, 
    public apiService: ApiService, 
    public router: Router, 
    public authService: AuthService) { }

  ngOnInit() {
    this.isNativePlatform = Capacitor.isNativePlatform();

    var todaysDate = new Date();

    // Build Invoice Form
    this.ionicForm = this.formBuilder.group({
      contactId: ['', [Validators.required]],
      businessId: ['', [Validators.required]],
      propertyId: [''],
      customer: {},
      business: {},
      property: {},
      currency: 'GBP',
      invoice_date: todaysDate.toISOString(),
      description: '',
      title: '',
      createQuote: false,
      createInvoice: false
    })

    this.newJob = new Job().deserialize({});

    // Get selected Account currency
    this.authService.getSelectedAccount().then((account) => {
      this.accountCurrency = account.currency;
      // Patch the form with the currency
      this.ionicForm.patchValue(
        {
          currency: account.currency
        }
      );
    });

    // If we have a contact ID, then we should pre-populate the form
    if (this.contactId)
      this.setupContactForm(this.contactId);

    // Get businesses and if we have 1 business, then pre-populate the form
    this.apiService.getBusinessList().subscribe(response => {
      if (response.length === 1) {
        this.ionicForm.patchValue(
          {
            businessId: response[0].id,
            business: {
              name: response[0].name
            }
          }
        );

        // Update model
        this.newJob.businessId = response[0].id;
      }
    });

  }

  dismiss() {  
    this.modalCtrl.dismiss();
  }

  setupContactForm(contactId) {
    // Get contact
    this.apiService.getContact(contactId).subscribe(response => {

      // Pre-populate form values
      this.ionicForm.patchValue(
        {
          contactId: response.id,
          customer: {
            title: response.title,
            first_name: response.first_name,
            last_name: response.last_name,
            mainName: response.mainName,
            email: response.email,
            phone1: response.phone1,
            phone2: response.phone2,
            address1: response.address1,
            address2: response.address2,
            city: response.city,
            postcode: response.postcode
          }
        }
      );

      // Update model
      this.newJob.contactId = response.id;

      // Fetch properties and pre-populate the property details from the contact
      this.apiService.getPropertyListByContact(response.id).subscribe(response => {

        // If there are no properties, then we should ensure that the form is reset for properties
        this.ionicForm.patchValue(
        {
          propertyId: '',
          property: {}
        });

        // Create a properties array for the picker with ID and address 1
        this.properties = response.map(function(p) {
          return {
            text: p.address1,
            value: p.id
          }
        });

      })
    });
  }

  async chooseCustomerModal() {
    const modal = await this.modalCtrl.create({  
      component: ContactsSelectorComponent,
      canDismiss: true,
      presentingElement: await this.modalCtrl.getTop(), // Get the top-most ion-modal
      componentProps: {  },
    });
   
    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {

      console.log(data);

      this.setupContactForm(data.id);

    }
  }

  async openPropertyChooser() {
    const modal = await this.modalCtrl.create({  
      component: PropertyChooserComponent,
      canDismiss: true,
      presentingElement: await this.modalCtrl.getTop(), // Get the top-most ion-modal
      componentProps: { contactId: this.newJob.contactId },
    });
   
    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      // Update this model
      this.newJob.propertyId = data.id;

      // Patch the form with the property ID
      this.ionicForm.patchValue(
        {
          propertyId: data.id,
          property: {
            name: data.address1
          }
        }
      );

    }
  }

  async chooseBusiness() {
    const modal = await this.modalCtrl.create({  
      component: BusinessSelectorComponent,
      canDismiss: true,
      presentingElement: await this.modalCtrl.getTop(), // Get the top-most ion-modal
      componentProps: {  },
    });
   
    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {

      this.ionicForm.patchValue(
        {
          businessId: data.id,
          business: {
            name: data.name
          }
        }
      );

      // Set Business to get invoice number preview
      this.chosenBusiness = data;

      // Update model
      this.newJob.businessId = data.id;
    }
  }

  // async submitForm() {
  //   this.isSubmitted = true;

  //   if (!this.ionicForm.valid) {
  //     console.log('Please provide all the required values for a job.')
  //     return false;
  //   } else {

  //     // Prep the Invoice data
  //     const newJob = this.newJob;

  //     // Description
  //     newJob.description = this.ionicForm.get('description').value;

  //     // Title
  //     newJob.title = this.ionicForm.get('title').value;

  //     // API Call
  //     this.apiService.createJob(newJob).subscribe((response) => {
  //       // Job saved
  //       let jobId = response.id;

  //       // Now we need to save an invoice if chosen
  //       if (this.ionicForm.get('createInvoice').value) {
  //         this.newInvoice = new Invoice().deserialize({
  //           jobId: jobId,
  //           businessId: this.ionicForm.get('businessId').value,
  //           currency: this.ionicForm.get('currency').value,
  //           invoice_date: this.ionicForm.get('invoice_date').value,
  //           description: this.ionicForm.get('description').value
  //         });

  //         this.apiService.createInvoice(this.newInvoice).subscribe((response) => {
  //           console.log('Invoice created');
  //         });
  //       }

  //       // Next save a quote if chosen
  //       if (this.ionicForm.get('createQuote').value) {
  //         this.newQuote = new Quote().deserialize({
  //           jobId: jobId,
  //           businessId: this.ionicForm.get('businessId').value,
  //           currency: this.ionicForm.get('currency').value,
  //           description: this.ionicForm.get('description').value
  //         });

  //         this.apiService.createQuote(this.newQuote).subscribe((quoteResponse) => {
  //           console.log('Quote created');
            
  //           // Update Job with quoteId
  //           this.apiService.updateJob(jobId, { quoteId: quoteResponse.id }).subscribe((response) => {
  //             console.log('Job updated with quoteId');
  //           });
  //         });
  //       }

  //       this.router.navigateByUrl('/t/jobs/view/'+response.id);
  //     });

  //     // Close the modal
  //     this.modalCtrl.dismiss(this.ionicForm.value); // TODO: Return data, although we should refresh previous view / form
  //   }
  // }

  async submitForm() {
    if (this.isProcessing) {
      return; // Prevent duplicate form submissions
    }
    this.isProcessing = true;
  
    this.isSubmitted = true;
  
    if (!this.ionicForm.valid) {
      console.log('Please provide all the required values for a job.');
      this.isProcessing = false;
      return false;
    }
  
    try {
      // Prepare the job data
      const newJob = this.newJob;
      newJob.description = this.ionicForm.get('description').value;
      newJob.title = this.ionicForm.get('title').value;
  
      // Create the job
      const jobResponse = await this.apiService.createJob(newJob).toPromise();
      const jobId = jobResponse.id;
  
      const apiPromises: Promise<any>[] = [];
  
      if (this.ionicForm.get('createInvoice').value) {
        const newInvoice = new Invoice().deserialize({
          jobId: jobId,
          businessId: this.ionicForm.get('businessId').value,
          currency: this.ionicForm.get('currency').value,
          invoice_date: this.ionicForm.get('invoice_date').value,
          description: this.ionicForm.get('description').value,
          contactId: this.ionicForm.get('contactId').value,
        });
  
        const invoiceWorkItems = JSON.parse(JSON.stringify(this.workItems));
        apiPromises.push(this.apiService.createInvoiceWithWorkItems(newInvoice, invoiceWorkItems).toPromise());
      }
  
      if (this.ionicForm.get('createQuote').value) {
        const newQuote = new Quote().deserialize({
          jobId: jobId,
          businessId: this.ionicForm.get('businessId').value,
          currency: this.ionicForm.get('currency').value,
          description: this.ionicForm.get('description').value,
          contactId: this.ionicForm.get('contactId').value,
        });
  
        const quoteWorkItems = JSON.parse(JSON.stringify(this.workItems));
        const quotePromise = this.apiService
          .createQuoteWithWorkItems(newQuote, quoteWorkItems)
          .toPromise()
          .then((response) => {
            return this.apiService.updateJob(jobId, { quoteId: response.quote.id }).toPromise();
          });
  
        apiPromises.push(quotePromise);
      }
  
      await Promise.all(apiPromises);
  
      await this.router.navigateByUrl('/t/jobs/view/' + jobId);
      this.modalCtrl.dismiss(this.ionicForm.value);
    } catch (error) {
      console.error('Error during form submission:', error);
    } finally {
      this.isProcessing = false; // Reset the processing flag
    }
  }

  get errorControl() {
    return this.ionicForm.controls;
  }

  get customerDisplay() {
    if (this.ionicForm.get('customer').value.mainName) {
      return this.ionicForm.get('customer').value.mainName+' - '+this.ionicForm.get('customer').value.email;
    }
    else {
      return 'Choose a customer';
    }
  }

  get businessDisplay() {
    if (this.ionicForm.get('business').value.name) {
      return this.ionicForm.get('business').value.name;
    }
    else {
      return 'Choose a Business';
    }
  }

  get propertyDisplay() {
    if (this.ionicForm.get('property').value.name) {
      return this.ionicForm.get('property').value.name;
    }
    else {
      return 'Choose a Visit Address (optional)';
    }
  }

  // Invoice, Quote & Work Items
  

}
